/**
 * RBAC permission matrix — browser-safe mirror of
 * `packages/core/src/rbac/matrix.ts` (canonical v1).
 *
 * Why duplicate? `@ferro/core/rbac` re-exports through index.ts which
 * pulls in Express, Prisma, jsonwebtoken — none of which can ship to
 * the browser. The matrix itself is pure constants, so a small
 * duplicate is cheaper than carving a sub-path export and convincing
 * Vite to tree-shake the right things.
 *
 * Lock-step contract: when the server matrix changes, this file MUST
 * change in the same commit. This is enforced by an EXECUTABLE test —
 * the `lock-step with @ferro/core canonical matrix` block in
 * `src/__tests__/rbac-matrix.test.ts` imports the canonical
 * `@ferro/core/src/rbac/matrix.js` (pure constants, node-only) and
 * asserts per-role permission-set equality, so a one-sided edit fails
 * CI. If we ever need a third app to consume the matrix, promote it to
 * a `@ferro/rbac-shared` package.
 */

export const ROLES = [
  'owner',
  'manager',
  'receptionist',
  'walker',
  'groomer',
  'kennel_tech',
  'accountant',
] as const;
export type RoleSlug = (typeof ROLES)[number];

export const PLATFORM_ADMIN_ROLE = 'platform_admin' as const;

const OWNER_PERMISSIONS = [
  'customers:read:all',
  'customers:write:all',
  'customers.notes:write:all', // #715 — append-only note on the parent
  'customers:delete:all',
  'customers:export:all',
  'pets:read:all',
  'pets.notes:write:all', // #649 — append-only observations
  'pets.meds-feeding:write:all', // Sawyer 08 meds-feeding (G3) — staff meds + feeding edits
  'pets:write:all',
  'pets:delete:all',
  'bookings.daycare-boarding:read:all',
  'bookings.daycare-boarding:write:all',
  'bookings.walking:read:all',
  'bookings.walking:write:all',
  'bookings.walking:reassign:all',
  'bookings.grooming:read:all',
  'bookings.grooming:write:all',
  'payments:read:all',
  'payments:write:all',
  'payments:refund:all',
  // LG (#1114 AC 8) — confirm a reservation HELD for staff review (intact
  // pair / missing agreement) with a logged reason. Owner + manager only;
  // checked in-handler against this matrix (see booking/gates/review-hold.ts).
  'bookings.review-hold:override:all',
  'payments-ops:read:all',
  'payments-ops:write:all',
  // #233 — gateway credential set/rotate (owner-only; mirrors core matrix)
  'payments-ops.credentials:write:all',
  'revenue:read:all',
  'revenue.transactions:read:all',
  'revenue.payouts:read:all',
  'payroll:read:all',
  'payroll.rates:write:all',
  'payroll.clock:write:self',
  'payroll.clock:write:all',
  'tips:read:all',
  'tips:write:self',
  'staff-schedule:read:all',
  'staff-schedule:write:all',
  // Time off (#427 W2) — the owner works here too, and approves everyone else's
  'time-off:request:self',
  'time-off:manage:all',
  // Sawyer 16 staff-messaging (E4) — #754, staff messaging: every employee reads and posts
  'staff-messages:read:all',
  'staff-messages:write:all',
  'rbac-admin:write:all',
  'rbac-audit:read:all',
  'incidents:read:all',
  'incidents:write:all',
  'incidents.owner-only:write:all',
  'vaccinations:read:all',
  'vaccinations:write:all',
  'business-config:read:all',
  'business-config:write:all',
  // Sawyer 04 manager-beds (B3) — beds (Settings → Lodging), inside the general write
  'business-config.lodging:write:all',
  // SPEC 2026-10-26 (D1) — the required-vaccine list, inside the general write
  'business-config.vaccinations:write:all',
  'business-config.pricing:write:all',
  'business-config.branding:write:all',
  'contracts.platform:read:all',
  'contracts.customer:read:all',
  'contracts.customer:write:all',
  'messaging:write:all',
  'messaging.ai-config:write:all',
  'ai-manager:use:all',
  'ai-manager:manage:all',
  'audit-log:read:all',
  'dashboard:read:all',
] as const;

const MANAGER_PERMISSIONS = [
  'customers:read:all',
  'customers:write:all',
  'customers.notes:write:all', // #715 — append-only note on the parent
  'pets:read:all',
  'pets.notes:write:all', // #649 — append-only observations
  'pets.meds-feeding:write:all', // Sawyer 08 meds-feeding (G3) — staff meds + feeding edits
  'pets:write:all',
  'bookings.daycare-boarding:read:all',
  'bookings.daycare-boarding:write:all',
  'bookings.walking:read:all',
  'bookings.walking:write:all',
  'bookings.walking:reassign:all',
  'bookings.grooming:read:all',
  'bookings.grooming:write:all',
  'payments:read:partial',
  'payments:write:all',
  'payments:refund:all',
  // LG (#1114 AC 8) — confirm a reservation HELD for staff review (intact
  // pair / missing agreement) with a logged reason. Owner + manager only;
  // checked in-handler against this matrix (see booking/gates/review-hold.ts).
  'bookings.review-hold:override:all',
  'payments-ops:read:all',
  'payments-ops:write:all',
  'revenue:read:all',
  'payroll:read:all',
  'payroll.clock:write:self',
  'payroll.clock:write:all',
  'tips:read:all',
  'tips:write:self',
  'staff-schedule:read:all',
  'staff-schedule:write:all',
  // Time off (#427 W2) — managers run the approve/deny queue
  'time-off:request:self',
  'time-off:manage:all',
  // Sawyer 16 staff-messaging (E4) — #754, staff messaging: every employee reads and posts
  'staff-messages:read:all',
  'staff-messages:write:all',
  'rbac-audit:read:all',
  'incidents:read:all',
  'incidents:write:all',
  'vaccinations:read:all',
  'vaccinations:write:all',
  'business-config:read:all',
  // #933 D2 (2026-10-30) — the general Settings write; money and AI switches stay owner
  'business-config:write:all',
  // Sawyer 04 manager-beds (B3) — beds (Settings → Lodging)
  'business-config.lodging:write:all',
  // SPEC 2026-10-26 (D1) — the required-vaccine list
  'business-config.vaccinations:write:all',
  'contracts.customer:read:all',
  'contracts.customer:write:all',
  'messaging:write:all',
  'messaging.ai-config:read:all',
  'ai-manager:use:all',
  'ai-manager:manage:all',
  'dashboard:read:scoped',
] as const;

const RECEPTIONIST_PERMISSIONS = [
  'customers:read:all',
  'customers:write:all',
  'customers.notes:write:all', // #715 — append-only note on the parent
  'pets:read:all',
  'pets.notes:write:all', // #649 — append-only observations
  'pets.meds-feeding:write:all', // Sawyer 08 meds-feeding (G3) — staff meds + feeding edits
  'pets:write:all',
  'bookings.daycare-boarding:read:scoped',
  'bookings.daycare-boarding:write:all',
  'bookings.walking:read:scoped',
  'bookings.walking:write:all',
  'bookings.grooming:read:scoped',
  'bookings.grooming:write:all',
  'payments:read:self',
  'payments:write:all',
  'payments-ops:read:all',
  'payments-ops:write:all',
  'payroll.clock:write:self',
  'tips:read:self',
  'staff-schedule:read:all',
  // Time off (#427 W2) — can ask; cannot decide
  'time-off:request:self',
  // Sawyer 16 staff-messaging (E4) — #754, staff messaging: every employee reads and posts
  'staff-messages:read:all',
  'staff-messages:write:all',
  'incidents:read:all',
  'incidents:write:all',
  'vaccinations:read:all',
  'vaccinations:write:all',
  'business-config:read:all',
  'contracts.customer:read:all',
  'messaging:write:all',
  'ai-manager:use:all',
  'dashboard:read:scoped',
] as const;

const WALKER_PERMISSIONS = [
  'pets:read:self',
  'pets.notes:write:all', // #649 — append-only observations
  'bookings.walking:read:self',
  'bookings.walking:write:self',
  'payroll.clock:write:self',
  'tips:read:self',
  'tips:write:self',
  // #665 P1 D1 — floor staff see the whole PUBLISHED schedule (drafts stay
  // write-gated server-side; /my-schedule narrows via ?self=1). Mirrors the
  // canonical matrix — supersedes the W0-A :read:self grant.
  'staff-schedule:read:all',
  // Time off (#427 W2)
  'time-off:request:self',
  // Sawyer 16 staff-messaging (E4) — #754, staff messaging: every employee reads and posts
  'staff-messages:read:all',
  'staff-messages:write:all',
  'incidents:read:self',
  'incidents:write:self',
  'vaccinations:read:self',
  'dashboard:read:scoped',
] as const;

const GROOMER_PERMISSIONS = [
  'pets:read:self',
  'pets.notes:write:all', // #649 — append-only observations
  'bookings.grooming:read:self',
  'bookings.grooming:write:self',
  'payments:read:self',
  'payments:write:self',
  'payroll.clock:write:self',
  'tips:read:self',
  'tips:write:self',
  // #665 P1 D1 — full PUBLISHED-schedule visibility (see walker note).
  'staff-schedule:read:all',
  // Time off (#427 W2)
  'time-off:request:self',
  // Sawyer 16 staff-messaging (E4) — #754, staff messaging: every employee reads and posts
  'staff-messages:read:all',
  'staff-messages:write:all',
  'incidents:read:self',
  'incidents:write:self',
  'vaccinations:read:self',
  'dashboard:read:scoped',
] as const;

// Kennel-tech / yard worker (#134) — floor care, :self shape, no money.
// MUST stay set-equal to KENNEL_TECH_PERMISSIONS in the canonical
// packages/core/src/rbac/matrix.ts (lock-step test enforces it).
const KENNEL_TECH_PERMISSIONS = [
  'pets:read:self',
  'pets.notes:write:all', // #649 — append-only observations
  'pets.meds-feeding:write:all', // Sawyer 08 meds-feeding (G3) — staff meds + feeding edits
  'bookings.daycare-boarding:read:self',
  'vaccinations:read:self',
  'incidents:read:self',
  'incidents:write:self',
  'payroll.clock:write:self',
  // Schedule — #427 W2 granted :read:self (closes #400: kennel_tech held NO
  // staff-schedule permission at all, so Dezirea could not see her own
  // shifts); #665 P1 D1 upgrades to full PUBLISHED-schedule visibility
  // (see walker note).
  'staff-schedule:read:all',
  // Time off (#427 W2)
  'time-off:request:self',
  // Sawyer 16 staff-messaging (E4) — #754, staff messaging: every employee reads and posts
  'staff-messages:read:all',
  'staff-messages:write:all',
  'dashboard:read:scoped',
] as const;

// Accountant / bookkeeper (#135) — read-only money observer, no write/refund.
// MUST stay set-equal to ACCOUNTANT_PERMISSIONS in the canonical matrix.
const ACCOUNTANT_PERMISSIONS = [
  'customers:read:all',
  'customers:export:all',
  'payments:read:all',
  'payments-ops:read:all',
  'revenue:read:all',
  'revenue.transactions:read:all',
  'revenue.payouts:read:all',
  'payroll:read:all',
  'tips:read:all',
  'business-config:read:all',
  'audit-log:read:all',
  // #665 P1 — closes the accountant dead end (time-off:request:self with no
  // schedule surface to exercise it from): their OWN rows on /my-schedule.
  'staff-schedule:read:self',
  // Time off (#427 W2) — the accountant is an employee; asking for a day off
  // is not the same as punching a clock (which they deliberately cannot do).
  'time-off:request:self',
  // Sawyer 16 staff-messaging (E4) — #754, staff messaging: the accountant READS the board and
  // any thread opened with them, and does not post (most conservative
  // default, an open HTHD question; reversing it is one seed row + this line).
  'staff-messages:read:all',
  'dashboard:read:scoped',
] as const;

export const ROLE_PERMISSIONS: Record<RoleSlug, ReadonlyArray<string>> = {
  owner: OWNER_PERMISSIONS,
  manager: MANAGER_PERMISSIONS,
  receptionist: RECEPTIONIST_PERMISSIONS,
  walker: WALKER_PERMISSIONS,
  groomer: GROOMER_PERMISSIONS,
  kennel_tech: KENNEL_TECH_PERMISSIONS,
  accountant: ACCOUNTANT_PERMISSIONS,
};

const ROLE_PERMISSION_SETS: Record<RoleSlug, ReadonlySet<string>> = {
  owner: new Set(OWNER_PERMISSIONS),
  manager: new Set(MANAGER_PERMISSIONS),
  receptionist: new Set(RECEPTIONIST_PERMISSIONS),
  walker: new Set(WALKER_PERMISSIONS),
  groomer: new Set(GROOMER_PERMISSIONS),
  kennel_tech: new Set(KENNEL_TECH_PERMISSIONS),
  accountant: new Set(ACCOUNTANT_PERMISSIONS),
};

function isRoleSlug(value: string | null | undefined): value is RoleSlug {
  return (
    value === 'owner' ||
    value === 'manager' ||
    value === 'receptionist' ||
    value === 'walker' ||
    value === 'groomer' ||
    value === 'kennel_tech' ||
    value === 'accountant'
  );
}

/**
 * Pure check: does this role grant this permission?
 *
 * `platform_admin` always returns `true` — they sit above the tenant
 * matrix per ADR-2026-04-28 (control-plane separation). Tenant code
 * paths shouldn't render to a platform admin in the first place, but
 * if they do, the platform admin sees everything.
 *
 * Unknown roles (typo, future role not yet shipped) return `false` —
 * fail closed. This matters: if a tenant ships with a 'super-receptionist'
 * role we haven't taught the UI about, we hide controls instead of
 * accidentally revealing them.
 */
export function roleHasPermission(
  role: string | null | undefined,
  permission: string,
): boolean {
  if (role === PLATFORM_ADMIN_ROLE) return true;
  if (!isRoleSlug(role)) return false;
  return ROLE_PERMISSION_SETS[role].has(permission);
}

/**
 * #428 — multi-role union check (mirror of the canonical helper in
 * `packages/core/src/rbac/matrix.ts`): does ANY of these roles grant
 * this permission? Empty / null / undefined ⇒ false (fail closed);
 * a one-element list is behaviorally identical to `roleHasPermission`.
 */
export function rolesHavePermission(
  roles: ReadonlyArray<string | null | undefined> | null | undefined,
  permission: string,
): boolean {
  if (!roles || roles.length === 0) return false;
  return roles.some((role) => roleHasPermission(role, permission));
}

/**
 * #428 — normalize a staff identity (`{ role, roles? }` — the session
 * StaffUser or any test shape) to its effective-role list. Prefers the
 * server-issued `roles` claim (primary first, then grants); falls back
 * to `[role]` for pre-#428 sessions so single-role behavior is
 * byte-identical. Mirror of the canonical core helper.
 */
export function effectiveRolesOf(
  staff:
    | { role?: string | null; roles?: readonly string[] | null }
    | null
    | undefined,
): ReadonlyArray<string> {
  if (!staff) return [];
  if (Array.isArray(staff.roles) && staff.roles.length > 0) {
    return staff.roles.filter(
      (r): r is string => typeof r === 'string' && r.length > 0,
    );
  }
  return typeof staff.role === 'string' && staff.role.length > 0
    ? [staff.role]
    : [];
}

/**
 * #428 — is this slug grantable as an ADDITIONAL role? Tenant roles
 * minus `owner` (owner stays invite/primary-only; platform_admin is
 * control-plane and already fails `isRoleSlug`). Mirror of the
 * canonical core helper; the per-account "not the primary" rule is the
 * server's 409.
 */
export function isGrantableRoleSlug(
  value: string | null | undefined,
): value is Exclude<RoleSlug, 'owner'> {
  return isRoleSlug(value) && value !== 'owner';
}
